


























import Vue, { PropType } from 'vue'
import {
  CategoryId,
  TreeLevelCategory,
  TreeLevelCategoryResult,
  TreeLevelNode
} from '~/models/category/types'
import CCategoryLevel from './CCategoryLevel.vue'
import { findDeep } from '~/utils/object'
import CategoryTreeLevelService from '~/services/category/CategoryTreeLevelService'

export default Vue.extend({
  components: { CCategoryLevel },
  props: {
    categoryId: {
      type: Number,
      required: false,
      default: CategoryId.CLASSIFIEDS
    },
    rootCategoryIds: {
      type: Array as PropType<number[]>,
      required: false,
      default() {
        return [CategoryId.CLASSIFIEDS]
      }
    },
    hiddenLevel: {
      type: Number,
      required: false,
      default: null
    },
    nodes: {
      type: Array as PropType<TreeLevelNode[]>,
      default() {
        return []
      }
    },
    selectButtonInCategory: {
      type: Boolean,
      default: false
    },
    displayRecents: {
      type: Boolean,
      default: false
    },
    inEdit: {
      type: Boolean,
      default: false
    },
    multiMode: {
      type: Boolean,
      default: false
    },
    editCollapse: {
      type: Boolean,
      default: false
    },
    showJobsLink: {
      type: Boolean,
      required: false,
      default: false
    },
    showPlotLink: {
      type: Boolean,
      required: false,
      default: false
    },
    isPrimary: {
      type: Boolean,
      required: false,
      default: false
    },
    showCategoryId: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      categoryLevels: [] as TreeLevelCategory[][]
    }
  },
  computed: {
    selectedCategoryId: {
      get() {
        return (
          this.categoryId ||
          (this.rootCategoryIds.length && this.rootCategoryIds[0])
        )
      },
      set(category: number) {
        this.loadCategoryLevels(category)
      }
    },
    recentCategory(): Pick<
      TreeLevelCategory,
      'hasChildren' | 'humanName' | 'id' | 'isActive'
    > {
      return {
        hasChildren: true,
        humanName: this.$i18n.t('recents') as string,
        id: -1,
        isActive: this.categoryId === -1
      }
    },
    firstShowingLevel(): number {
      if (this.hiddenLevel === null) {
        return 0
      }

      for (let i = 0; i < this.categoryLevels.length; i++) {
        if (i !== this.hiddenLevel) {
          return i
        }
      }

      return 0
    }
  },
  watch: {
    categoryId: {
      async handler(newCategory: number) {
        await this.loadCategoryLevels(newCategory || this.rootCategoryIds[0])
      }
    }
  },
  mounted() {
    this.loadCategoryLevels(this.categoryId || this.rootCategoryIds[0])
  },
  methods: {
    handleCategoryClick(category: TreeLevelCategory) {
      if (category.movedToCategoryUrl) {
        return
      }
      this.$emit('change', category)
    },
    handleCategoryClear(category: TreeLevelCategory) {
      const parentCategory = this.categoryLevels
        .flat()
        .find(childCategory => childCategory.id === category.parentId)
      this.$emit('change', parentCategory || this.rootCategoryIds[0])
    },
    shouldDisplayRecentCategories(categoryId: number): boolean {
      if (
        !this.displayRecents ||
        this.categoryLevels.length <= 2 ||
        this.rootCategoryIds.length > 1 // Block recents for multiple roots due to unperformed qa to existing use cases.
      ) {
        return false
      }

      return this.categoryLevels[2].some(c => c.id === categoryId && c.isRecent)
    },
    activateRecentCategory(categoryId: number) {
      Vue.set(
        this.categoryLevels,
        2,
        this.categoryLevels[2].map(c => ({
          ...c,
          isActive: c.id === categoryId && c.isRecent
        }))
      )
    },
    async loadCategoryLevels(
      categoryId: number = this.categoryId,
      external = false
    ) {
      if (this.shouldDisplayRecentCategories(categoryId)) {
        this.activateRecentCategory(categoryId)
        return
      }

      const [categoryTree, recentCategories] = await Promise.all(
        this.getCategoryRequests(categoryId)
      )

      const { levels, hasRecentCategories } = categoryTree
      if (this.displayRecents && levels[1]) {
        // @ts-ignore
        levels[1].unshift({
          ...this.recentCategory,
          disabled: !hasRecentCategories,
          hasChildren: hasRecentCategories
        })
        recentCategories && categoryId === -1 && levels.push(recentCategories)
      }

      this.categoryLevels = levels

      const fetchedRootCategory = findDeep({
        object: this.categoryLevels,
        key: 'id',
        value: external ? categoryId : this.categoryId
      })

      if (external) {
        return fetchedRootCategory
      } else {
        this.$emit('load', fetchedRootCategory)
      }
    },
    getCategoryRequests(
      categoryId: number
    ): [
      Promise<TreeLevelCategoryResult>,
      Promise<TreeLevelCategory[] | undefined>
    ] {
      const categoryTreeLevelService = this.$dep(CategoryTreeLevelService)
      const shouldFetchRecentCategories: boolean = Boolean(
        categoryId === -1 &&
          this.displayRecents &&
          this.rootCategoryIds.length === 1
      )

      return [
        categoryTreeLevelService.getCategoryTreeLevels(
          this.rootCategoryIds,
          shouldFetchRecentCategories
            ? this.rootCategoryIds[0]
            : (categoryId => {
                if (this.nodes.some(node => node.id === categoryId)) {
                  return undefined
                }

                return shouldFetchRecentCategories
                  ? this.rootCategoryIds[0]
                  : categoryId
              })(categoryId),
          {
            rootUrlParam: this.rootCategoryIds.length === 1 // send root url param only when single root provided
          }
        ),
        shouldFetchRecentCategories
          ? categoryTreeLevelService.fetchRecentCategoryLevel(
              this.rootCategoryIds[0]
            )
          : Promise.resolve(undefined)
      ]
    },
    getNodesOfLevel(categories: TreeLevelCategory[], levelIndex: number) {
      return [
        ...categories,
        ...this.nodes.filter(node => this.nodeBelongsToLevel(node, levelIndex))
      ]
    },
    nodeBelongsToLevel(node: TreeLevelNode, levelIndex: number): boolean {
      if (typeof node.parentId === 'undefined') {
        const firstNonEmptyLevelIndex = this.categoryLevels.findIndex(
          lvl => lvl.length
        )
        return firstNonEmptyLevelIndex === levelIndex
      }

      const parentLevelIndex = levelIndex - 1
      return this.categoryLevels[parentLevelIndex]?.some(
        c => c.id === node.parentId && node.isActive
      )
    }
  }
})
